import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchInput from '../components/SearchInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { CalendarClock, Edit2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const NONE = '__none__';

const initialForm = {
  code: '',
  taskType: 'VISITA',
  ownerId: '',
  customerId: NONE,
  territoryId: NONE,
  title: '',
  detail: '',
  startAt: '',
  endAt: '',
  recurrenceEndAt: '',
  interactionOrigin: 'APP',
  interactionPlace: '',
  alarmMinutes: '0',
  public: false,
  weekDays: '',
  fixedDay: '',
  recurrence: '0',
  redirect: '0',
  redirectEnabled: false,
  weekMonth: '',
  estimatedDuration: '',
  estimatedCost: '',
  sequence: '',
  allowEdit: true,
  allowDelete: true,
  interactionGroup: '',
  cronExpression: '',
  cronTimezone: 'America/Sao_Paulo',
  scheduleMode: 'none',
  customTaskType: '',
};

const toLocalInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const toIso = (value) => value ? new Date(value).toISOString() : undefined;

const buildCron = (startAt, scheduleMode) => {
  if (!startAt || scheduleMode === 'none') return null;
  const date = new Date(startAt);
  const minute = date.getMinutes();
  const hour = date.getHours();
  if (scheduleMode === 'daily') return `${minute} ${hour} * * *`;
  if (scheduleMode === 'weekly') return `${minute} ${hour} * * ${date.getDay()}`;
  if (scheduleMode === 'monthly') return `${minute} ${hour} ${date.getDate()} * *`;
  return null;
};

const VisitConfigsPage = () => {
  const { user, isAdmin } = useAuth();
  const [visits, setVisits] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadData = async () => {
    try {
      const [visitResponse, customerResponse, territoryResponse] = await Promise.all([
        api.getVisitConfigs({ page: 1, limit: 100, search }),
        api.getCustomers({ page: 1, limit: 100 }),
        api.getTerritories({ page: 1, limit: 100 }),
      ]);
      setVisits(visitResponse.data || []);
      setCustomers(customerResponse.data || []);
      setTerritories(territoryResponse.data || []);

      if (isAdmin()) {
        const userResponse = await api.getUsers({ page: 1, limit: 100 });
        setUsers(userResponse.data || []);
      } else if (user) {
        setUsers([user]);
      }
    } catch (error) {
      toast.error('Erro ao carregar configurações de visita');
    }
  };

  const openDialog = (visit = null) => {
    setEditing(visit);
    setFormData(visit ? {
      code: visit.code,
      taskType: visit.taskType,
      ownerId: visit.ownerId,
      customerId: visit.customerId || NONE,
      territoryId: visit.territoryId || NONE,
      title: visit.title,
      detail: visit.detail || '',
      startAt: toLocalInput(visit.startAt),
      endAt: toLocalInput(visit.endAt),
      recurrenceEndAt: toLocalInput(visit.recurrenceEndAt),
      interactionOrigin: visit.interactionOrigin,
      interactionPlace: visit.interactionPlace || '',
      alarmMinutes: String(visit.alarmMinutes || 0),
      public: Boolean(visit.public),
      weekDays: visit.weekDays || '',
      fixedDay: visit.fixedDay?.toString() || '',
      recurrence: String(visit.recurrence || 0),
      redirect: String(visit.redirect || 0),
      redirectEnabled: Boolean(visit.redirectEnabled),
      weekMonth: visit.weekMonth || '',
      estimatedDuration: visit.estimatedDuration?.toString() || '',
      estimatedCost: visit.estimatedCost?.toString() || '',
      sequence: visit.sequence?.toString() || '',
      allowEdit: Boolean(visit.allowEdit),
      allowDelete: Boolean(visit.allowDelete),
      interactionGroup: visit.interactionGroup || '',
      cronExpression: visit.cronExpression || '',
      cronTimezone: visit.cronTimezone || 'America/Sao_Paulo',
      scheduleMode: visit.cronExpression ? 'custom' : String(visit.recurrence || 0) === '0' ? 'none' : 'daily',
      customTaskType: '',
    } : { ...initialForm, ownerId: user?.id || '' });
    setDialogOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    const taskType = formData.taskType === 'OUTRO'
      ? (formData.customTaskType || 'OUTRO').toUpperCase()
      : formData.taskType;
    const selectedCustomer = customers.find((customer) => customer.id === formData.customerId);
    const selectedTerritory = territories.find((territory) => territory.id === formData.territoryId);
    const cronExpression = formData.scheduleMode === 'custom'
      ? formData.cronExpression || null
      : buildCron(formData.startAt, formData.scheduleMode);
    const payload = {
      ...formData,
      code: formData.code || undefined,
      taskType,
      title: `${taskType} - ${selectedCustomer?.name || selectedTerritory?.name || 'Agenda'}`,
      customerId: formData.customerId === NONE ? null : formData.customerId,
      territoryId: formData.territoryId === NONE ? null : formData.territoryId,
      startAt: toIso(formData.startAt),
      endAt: toIso(formData.endAt),
      recurrenceEndAt: toIso(formData.recurrenceEndAt) || null,
      alarmMinutes: Number(formData.alarmMinutes || 0),
      fixedDay: formData.fixedDay ? Number(formData.fixedDay) : null,
      recurrence: formData.scheduleMode === 'none' ? 0 : 1,
      cronExpression,
      cronTimezone: formData.cronTimezone || 'America/Sao_Paulo',
      redirect: Number(formData.redirect || 0),
      estimatedDuration: formData.estimatedDuration ? Number(formData.estimatedDuration) : null,
      estimatedCost: formData.estimatedCost ? Number(formData.estimatedCost) : null,
      sequence: formData.sequence ? Number(formData.sequence) : null,
      detail: formData.detail || null,
      interactionPlace: formData.interactionPlace || null,
      weekDays: formData.weekDays || null,
      weekMonth: formData.weekMonth || null,
      interactionGroup: formData.interactionGroup || null,
    };

    try {
      if (editing) {
        await api.updateVisitConfig(editing.id, payload);
        toast.success('Configuração de visita atualizada');
      } else {
        await api.createVisitConfig(payload);
        toast.success('Configuração de visita criada');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar configuração de visita');
    }
  };

  const remove = async (visit) => {
    if (!window.confirm('Remover esta configuração de visita?')) return;
    try {
      await api.deleteVisitConfig(visit.id);
      toast.success('Configuração de visita removida');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao remover configuração de visita');
    }
  };

  return (
    <div className="space-y-6" data-testid="visit-configs-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Configuração Visita</h1>
          <p className="text-muted-foreground mt-1">Configuração baseada na tabela InteractionConfig</p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Nova Configuração
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle>{editing ? 'Editar Configuração Visita' : 'Nova Configuração Visita'}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Código</Label>
                    <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} placeholder="Automático" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select value={formData.taskType} onValueChange={(value) => setFormData({ ...formData, taskType: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="VENDA">Venda</SelectItem>
                        <SelectItem value="VISITA">Visita</SelectItem>
                        <SelectItem value="ENTREGA">Entrega</SelectItem>
                        <SelectItem value="OUTRO">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.taskType === 'OUTRO' && (
                  <div className="space-y-2">
                    <Label>Outro tipo</Label>
                    <Input value={formData.customTaskType} onChange={(e) => setFormData({ ...formData, customTaskType: e.target.value.toUpperCase() })} required />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vendedor</Label>
                    <Select value={formData.ownerId} onValueChange={(value) => setFormData({ ...formData, ownerId: value })}>
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{users.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Carteira</Label>
                    <Select value={formData.territoryId} onValueChange={(value) => setFormData({ ...formData, territoryId: value })}>
                      <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Sem carteira</SelectItem>
                        {territories.map((item) => <SelectItem key={item.id} value={item.id}>{item.code} - {item.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Select value={formData.customerId} onValueChange={(value) => setFormData({ ...formData, customerId: value })}>
                    <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Sem cliente</SelectItem>
                      {customers.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Início</Label><Input type="datetime-local" value={formData.startAt} onChange={(e) => setFormData({ ...formData, startAt: e.target.value })} required /></div>
                  <div className="space-y-2"><Label>Fim</Label><Input type="datetime-local" value={formData.endAt} onChange={(e) => setFormData({ ...formData, endAt: e.target.value })} required /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Agenda</Label>
                    <Select value={formData.scheduleMode} onValueChange={(value) => setFormData({ ...formData, scheduleMode: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem recorrência</SelectItem>
                        <SelectItem value="daily">Diária</SelectItem>
                        <SelectItem value="weekly">Semanal</SelectItem>
                        <SelectItem value="monthly">Mensal</SelectItem>
                        <SelectItem value="custom">CRON manual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Fim recorrência</Label><Input type="datetime-local" value={formData.recurrenceEndAt} onChange={(e) => setFormData({ ...formData, recurrenceEndAt: e.target.value })} /></div>
                </div>
                {formData.scheduleMode === 'custom' && (
                  <div className="space-y-2">
                    <Label>CRON</Label>
                    <Input value={formData.cronExpression} onChange={(e) => setFormData({ ...formData, cronExpression: e.target.value })} placeholder="0 8 * * 1-5" />
                  </div>
                )}
                <Button type="submit" className="w-full">{editing ? 'Atualizar' : 'Cadastrar'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <SearchInput value={search} onChange={setSearch} placeholder="Pesquisar por código, título, cliente ou carteira..." searchFields={['Código', 'Título', 'Cliente', 'Carteira']} />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-semibold">Código</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Título</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Cliente</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Carteira</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Início</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visits.map((visit) => (
                <tr key={visit.id} className="border-b hover:bg-muted/50">
                  <td className="px-6 py-4 text-sm font-mono">{visit.code}</td>
                  <td className="px-6 py-4 text-sm font-medium">{visit.title}</td>
                  <td className="px-6 py-4 text-sm">{visit.customer?.name || '-'}</td>
                  <td className="px-6 py-4 text-sm">{visit.territory?.name || '-'}</td>
                  <td className="px-6 py-4 text-sm">{new Date(visit.startAt).toLocaleString('pt-BR')}</td>
                  <td className="px-6 py-4 text-sm text-center">
                    {isAdmin() ? <Switch checked={visit.enabled} onCheckedChange={() => api.toggleVisitConfig(visit.id).then(loadData)} /> : <Badge>{visit.enabled ? 'Ativa' : 'Inativa'}</Badge>}
                  </td>
                  <td className="px-6 py-4 text-sm text-right space-x-2">
                    {isAdmin() && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openDialog(visit)} disabled={!visit.allowEdit}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(visit)} disabled={!visit.allowDelete}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {visits.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CalendarClock className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhuma configuração de visita encontrada.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VisitConfigsPage;
